import AcceptancePolicyPage from './pages/Policies/AcceptancePolicyPage';
import TermsPolicyPage from './pages/Policies/TermsPolicyPage';
import PrivacyPolicyPage from './pages/Policies/PrivacyPolicyPage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import './App.css';
import MyOrdersPage from './pages/MyOrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import SupportPage from './pages/SupportPage';
import NotificationsPage from './pages/NotificationsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { useSEO, PAGE_SEO, buildProductSEO } from './hooks/useSEO';
import { API_BASE_URL } from './config/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const CART_STORAGE_KEY = 'tk_fashion_user_cart';
const USER_SESSION_KEY = 'tk_fashion_user_session';
const USER_SETTINGS_STORAGE_KEY = 'tk_fashion_user_settings';
const DEFAULT_GOOGLE_MAPS_API_KEY = 'AIzaSyAdUb1c8avkouWaD6FKj_6veKNAm6B1BzY';
const GHANAPOST_RESOLVE_URL = `${API_BASE_URL}/api/gps/resolve`;
const USER_GOOGLE_MAPS_SCRIPT_ID = 'tk-user-google-maps-script';

const GHANA_REGIONS = [
  'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern',
  'Greater Accra', 'North East', 'Northern', 'Oti', 'Savannah',
  'Upper East', 'Upper West', 'Volta', 'Western', 'Western North',
];

const DEFAULT_USER_SETTINGS = {
  fullName: '',
  phone: '',
  addressLine: '',
  addressGpsCode: '',
  addressResolved: '',
  addressLat: '',
  addressLng: '',
  addressPlaceId: '',
  city: '',
  region: 'Greater Accra',
  landmark: '',
  preferredDelivery: 'standard',
  preferredContact: 'phone',
  language: 'English',
  emailUpdates: true,
  orderAlerts: true,

};

const normalizeGhanaPostCode = (value) => value.trim().replace(/[\s-]+/g, '').toUpperCase();

const buildGhanaPostAddress = (row) => {
  const parts = [row?.Street, row?.Area, row?.District, row?.Region, 'Ghana'].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  return row?.AddressV1 || row?.GPSName || '';
};

const parseGhanaPostRow = (payload) => {
  const table =
    payload?.data?.Table ||
    payload?.Table ||
    payload?.data?.table ||
    payload?.table ||
    [];

  if (!Array.isArray(table) || table.length === 0) return null;
  return table[0];
};

const formatCurrency = (amount) => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return 'Contact for price';
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(amount);
};

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL}${url}`;
};

const parseJsonSafely = async (response) => {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { success: false, message: 'Something went wrong. Please try again.' };
  }
};

const registerUserRequest = async (input) => {
  const response = await fetch(`${API_BASE_URL}/api/users/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(payload.message || 'Unable to register right now');
  }

  return payload;
};

const loginUserRequest = async (input) => {
  const response = await fetch(`${API_BASE_URL}/api/users/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(payload.message || 'Invalid email or password');
  }

  return payload;
};

const googleAuthUserRequest = async (credential) => {
  const response = await fetch(`${API_BASE_URL}/api/users/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(payload.message || 'Google sign-in failed');
  }

  return payload;
};

const getUserProfileRequest = async (token) => {
  const response = await fetch(`${API_BASE_URL}/api/users/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(payload.message || 'Session has expired');
  }

  return payload;
};

const updateNotificationPrefsRequest = async (token, prefs) => {
  const response = await fetch(`${API_BASE_URL}/api/users/auth/me/preferences`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(prefs),
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(payload.message || 'Unable to save notification preferences');
  }

  return payload;
};

const deleteOwnAccountRequest = async (token, input) => {
  const response = await fetch(`${API_BASE_URL}/api/users/auth/me`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(payload.message || 'Unable to delete account right now');
  }

  return payload;
};

const isStrongUserPassword = (password) => {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[@#$%&]/.test(password)
  );
};

const getDetailedPrices = (product) => {
  const pricing = product?.pricing || {};
  const rows = [];

  if (pricing.mode === 'uniform') {
    if (typeof pricing.uniformRegularPrice === 'number') {
      rows.push({ label: 'Regular Price', value: pricing.uniformRegularPrice });
    }
    if (typeof pricing.uniformBulkPrice === 'number') {
      rows.push({
        label: `Bulk Price${pricing.uniformBulkMinQty ? ` (min ${pricing.uniformBulkMinQty})` : ''}`,
        value: pricing.uniformBulkPrice,
      });
    }
    if (typeof pricing.uniformExpressPrice === 'number') {
      rows.push({ label: 'Express Surcharge', value: pricing.uniformExpressPrice });
    }
  }

  if (pricing.mode === 'per-size' || pricing.mode === 'per-color') {
    (pricing.variationEntries || []).forEach((entry) => {
      rows.push({
        label: `${pricing.mode === 'per-size' ? 'Size' : 'Color'}: ${entry.key} (Regular)`,
        value: entry.regularPrice,
      });

      if (typeof entry.bulkPrice === 'number') {
        rows.push({
          label: `${pricing.mode === 'per-size' ? 'Size' : 'Color'}: ${entry.key} (Bulk${
            pricing.variationBulkMinQty ? ` min ${pricing.variationBulkMinQty}` : ''
          })`,
          value: entry.bulkPrice,
        });
      }
    });

    if (typeof pricing.variationExpressPrice === 'number') {
      rows.push({ label: 'Express Surcharge', value: pricing.variationExpressPrice });
    }
  }

  return rows;
};

const getPricingForSelection = (product, selection) => {
  const pricing = product?.pricing || {};
  const quantity = Math.max(1, Number(selection?.quantity || 1));

  const result = {
    unitPrice: null,
    tier: 'regular',
    note: 'Standard price',
  };

  if (pricing.mode === 'uniform') {
    if (typeof pricing.uniformRegularPrice === 'number') {
      result.unitPrice = pricing.uniformRegularPrice;
      result.note = 'Same price for all options';
    }

    if (
      typeof pricing.uniformBulkPrice === 'number' &&
      typeof pricing.uniformBulkMinQty === 'number' &&
      quantity >= pricing.uniformBulkMinQty
    ) {
      result.unitPrice = pricing.uniformBulkPrice;
      result.tier = 'bulk';
      result.note = `Bulk savings (buy ${pricing.uniformBulkMinQty}+ items)`;
    }

    return result;
  }

  if (pricing.mode === 'per-size') {
    const entry = (pricing.variationEntries || []).find((row) => row.key === selection.size);
    if (typeof entry?.regularPrice === 'number') {
      result.unitPrice = entry.regularPrice;
      result.note = `Selected size price: ${entry.key}`;
    }

    if (
      typeof entry?.bulkPrice === 'number' &&
      typeof pricing.variationBulkMinQty === 'number' &&
      quantity >= pricing.variationBulkMinQty
    ) {
      result.unitPrice = entry.bulkPrice;
      result.tier = 'bulk';
      result.note = `Bulk savings for size ${entry.key} (buy ${pricing.variationBulkMinQty}+ items)`;
    }

    return result;
  }

  if (pricing.mode === 'per-color') {
    const entry = (pricing.variationEntries || []).find((row) => row.key === selection.color);
    if (typeof entry?.regularPrice === 'number') {
      result.unitPrice = entry.regularPrice;
      result.note = `Selected color price: ${entry.key}`;
    }

    if (
      typeof entry?.bulkPrice === 'number' &&
      typeof pricing.variationBulkMinQty === 'number' &&
      quantity >= pricing.variationBulkMinQty
    ) {
      result.unitPrice = entry.bulkPrice;
      result.tier = 'bulk';
      result.note = `Bulk savings for color ${entry.key} (buy ${pricing.variationBulkMinQty}+ items)`;
    }

    return result;
  }

  return result;
};

const getExpressSurcharge = (product) => {
  if (!product?.delivery?.express?.enabled) return 0;

  if (typeof product?.pricing?.uniformExpressPrice === 'number') {
    return product.pricing.uniformExpressPrice;
  }

  if (typeof product?.pricing?.variationExpressPrice === 'number') {
    return product.pricing.variationExpressPrice;
  }

  return 0;
};

const buildCartKey = (productId, size, color) => {
  return `${productId}::${size || 'no-size'}::${color || 'no-color'}`;
};

const getDefaultSelection = (product) => ({
  size: product?.sizes?.[0] || '',
  color: product?.colors?.[0]?.name || '',
  quantity: 1,
});

const SOCIAL_PLATFORM_META = [
  { key: 'telegram', label: 'Telegram', image: '/social media images/telegram.jpg' },
  { key: 'telegramChannel', label: 'Telegram Channel', image: '/social media images/telegram channel.jpg' },
  { key: 'instagram', label: 'Instagram', image: '/social media images/instagram.jpg' },
  { key: 'tiktok', label: 'TikTok', image: '/social media images/tiktok.jpg' },
  { key: 'x', label: 'X', image: '/social media images/x.jpg' },
  { key: 'facebook', label: 'Facebook', image: '/social media images/facebook.jpg' },
  { key: 'youtube', label: 'YouTube', image: '/social media images/youtube.jpg' },
  { key: 'snapchat', label: 'Snapchat', image: '/social media images/snapchat.jpg' },
  { key: 'whatsappChannel', label: 'WhatsApp Channel', image: '/social media images/whatsapp channel.jpg' },
  { key: 'whatsapp1', label: 'WhatsApp 1', image: '/social media images/whatsapp.jpg' },
  { key: 'whatsapp2', label: 'WhatsApp 2', image: '/social media images/whatsapp.jpg' },
  { key: 'email1', label: 'Email 1', image: '/social media images/email.jpg' },
  { key: 'email2', label: 'Email 2', image: '/social media images/email.jpg' },
  { key: 'email3', label: 'Email 3', image: '/social media images/email.jpg' },
];

const buildSocialHref = (platformKey, rawValue) => {
  const value = String(rawValue || '').trim();
  if (!value) return '';
  if (platformKey.startsWith('email')) {
    // If value already starts with mailto:, use as is
    if (value.startsWith('mailto:')) return value;
    return `mailto:${value}`;
  }
  if (/^https?:\/\//i.test(value)) return value;

  const normalized = value.replace(/^@/, '').trim();

  switch (platformKey) {
    case 'telegram':
    case 'telegramChannel':
      return `https://t.me/${normalized}`;
    case 'instagram':
      return `https://www.instagram.com/${normalized}`;
    case 'tiktok':
      return `https://www.tiktok.com/${normalized.startsWith('@') ? normalized : `@${normalized}`}`;
    case 'x':
      return `https://x.com/${normalized}`;
    case 'facebook':
      return `https://www.facebook.com/${normalized}`;
    case 'youtube':
      return `https://www.youtube.com/${normalized}`;
    case 'snapchat':
      return `https://www.snapchat.com/add/${normalized}`;
    case 'whatsappChannel':
      return value;
    case 'whatsapp': {
      const digits = value.replace(/\D/g, '');
      return digits ? `https://wa.me/${digits}` : value;
    }
    default:
      return value;
  }
};

const getMediaItems = (product, type) => {
  const items = type === 'video' ? product?.videos || [] : product?.images || [];
  return items.map(resolveMediaUrl).filter(Boolean);
};

const ShopFooter = () => {
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadFooterSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/settings/shop/public`);
        const payload = await parseJsonSafely(response);
        if (!response.ok || cancelled) return;

        const source = payload?.settings?.socialLinks || {};
        const nextLinks = SOCIAL_PLATFORM_META.map((platform) => {
          const record = source?.[platform.key] || {};
          const href = buildSocialHref(platform.key, record.value);

          return {
            ...platform,
            enabled: Boolean(record.enabled),
            href,
          };
        }).filter((platform) => platform.enabled && platform.href);

        if (!cancelled) {
          setSocialLinks(nextLinks);
        }
      } catch {
        if (!cancelled) {
          setSocialLinks([]);
        }
      }
    };

    loadFooterSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer className="shop-footer">
      {/* Top: brand + social icons */}
      <div className="shop-footer-top">
        <div className="shop-footer-brand">
          <p className="shop-footer-eyebrow">TK Fashion &amp; Design</p>
          <p className="shop-footer-tagline">Premium styles for every occasion. Browse our latest stock and shop with confidence.</p>
        </div>

        {socialLinks.length > 0 && (
          <div className="shop-footer-social">
            <p className="shop-footer-social-label">Follow Us</p>
            <div className="shop-footer-social-list">
              {socialLinks.map((platform) => (
                <a
                  key={platform.key}
                  href={platform.href}
                  className="shop-footer-social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={platform.label}
                  aria-label={platform.label}
                >
                  <span className="shop-footer-social-icon">
                    <img src={platform.image} alt={platform.label} className="shop-footer-social-icon-image" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="shop-footer-divider" />

      {/* Middle: 3-column links */}
      <div className="shop-footer-links">
        <nav className="shop-footer-col" aria-label="Navigate">
          <p className="shop-footer-nav-heading">Navigate</p>
          <Link to="/" className="shop-footer-link">Shop</Link>
          <Link to="/cart" className="shop-footer-link">Cart</Link>
          <Link to="/checkout" className="shop-footer-link">Checkout</Link>
          <Link to="/orders" className="shop-footer-link">My Orders</Link>
          <Link to="/settings" className="shop-footer-link">Settings</Link>
        </nav>

        <div className="shop-footer-col">
          <p className="shop-footer-nav-heading">Policies</p>
          <Link to="/policies/acceptance" className="shop-footer-link">Acceptance Policy</Link>
          <Link to="/policies/terms" className="shop-footer-link">Terms &amp; Conditions</Link>
          <Link to="/policies/privacy" className="shop-footer-link">Privacy Policy</Link>
        </div>

        <div className="shop-footer-col">
          <p className="shop-footer-nav-heading">Support</p>
          <Link to="/support" className="shop-footer-link">💬 Live Support</Link>
          <Link to="/notifications" className="shop-footer-link">🔔 Notifications</Link>
          <a href="mailto:support@tkfashion.com" className="shop-footer-link">✉️ Email Us</a>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="shop-footer-bottom">
        <p>&copy; {new Date().getFullYear()} TK Fashion &amp; Design. All rights reserved.</p>
        <p className="shop-footer-location">📍 Accra, Ghana</p>
      </div>
    </footer>
  );
};

const HeaderBar = ({ cartSummary }) => (
  <header className="shop-hero">
    <div>
      <p className="shop-eyebrow">TK Fashion & Design</p>
      <h1>Shop The Latest Stock</h1>
      <p className="shop-subtitle">
        Discover premium styles, choose your variants, and add products to cart before checkout.
      </p>
    </div>

    <Link to="/cart" className="cart-pill-link" aria-label="Go to cart">
      <div className="cart-pill">
        <strong>{cartSummary.quantity}</strong>
        <span>Cart</span>
        <em>{formatCurrency(cartSummary.amount)}</em>
      </div>
    </Link>
  </header>
);

const SidebarNav = ({ cartSummary, isOpen, onClose, userSession, onLogout, unreadNotifCount }) => {
  const location = useLocation();

  const navItems = [
    { label: 'Shop', path: '/', icon: '🛍️' },
    { label: 'Cart', path: '/cart', icon: '🧺', badge: cartSummary.quantity },
    { label: 'Checkout', path: '/checkout', icon: '💳' },
    ...(userSession?.token ? [
      { label: 'My Orders', path: '/orders', icon: '📦' },
      { label: 'Notifications', path: '/notifications', icon: '🔔', badge: unreadNotifCount || 0 },
      { label: 'Settings', path: '/settings', icon: '⚙️' },
    ] : []),
  ];

  return (
    <>
      <aside id="user-sidebar-nav" className={`app-sidebar ${isOpen ? 'open' : ''}`}>
        <button
          type="button"
          className="sidebar-close-btn"
          onClick={onClose}
          aria-label="Close menu"
        >
          ×
        </button>
        <div className="sidebar-header-block">
          <p className="sidebar-eyebrow">TK Fashion & Design</p>
          <h2>Browse Store</h2>
          <p>Discover stock, review details, and manage your cart.</p>
          <div className="sidebar-user-chip" aria-live="polite">
            {userSession?.user
              ? `Hi, ${userSession.user.fullName || 'Customer'}`
              : 'Guest mode'}
          </div>
        </div>

        <p className="sidebar-nav-title">Navigation</p>
        <nav className="sidebar-nav" aria-label="Main navigation">
          {navItems.map((item) => {
            const isActive = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                onClick={onClose}
              >
                <span className="sidebar-link-icon">{item.icon}</span>
                <span className="sidebar-link-label">{item.label}</span>
                {typeof item.badge === 'number' && item.badge > 0 && (
                  <span className="sidebar-link-badge">{item.badge}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer-block">
          <div className="sidebar-auth-row">
            {userSession?.user ? (
              <>
                <small className="sidebar-auth-user">Signed in as {userSession.user.fullName}</small>
                <button type="button" className="sidebar-auth-btn" onClick={onLogout}>Log Out</button>
              </>
            ) : (
              <Link to="/login" className="sidebar-auth-btn" onClick={onClose}>Login</Link>
            )}
          </div>
        </div>
      </aside>

      {isOpen && <button type="button" className="sidebar-overlay" onClick={onClose} aria-label="Close menu" />}
    </>
  );
};

const UserSettingsPage = ({ cartSummary, userSession, settings, onSaveSettings, onDeleteAccount }) => {
  const [form, setForm] = useState(settings);
  const [message, setMessage] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteDetails, setDeleteDetails] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [googleAutocompleteReady, setGoogleAutocompleteReady] = useState(false);
  const [googleAutocompleteError, setGoogleAutocompleteError] = useState('');
  const [ghanaPostResolving, setGhanaPostResolving] = useState(false);
  const [ghanaPostError, setGhanaPostError] = useState('');
  const [ghanaPostSuccess, setGhanaPostSuccess] = useState('');
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const placeChangedListenerRef = useRef(null);
  const ghanapostDebounceRef = useRef(null);
  const ghanapostRequestRef = useRef(0);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  useEffect(() => {
    const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || DEFAULT_GOOGLE_MAPS_API_KEY || '')
      .trim()
      .replace(/^"(.*)"$/, '$1')
      .replace(/^'(.*)'$/, '$1');

    if (!apiKey) {
      setGoogleAutocompleteError('Address suggestions are temporarily unavailable.');
      return;
    }

    const markReady = () => {
      if (window.google?.maps?.places) {
        setGoogleAutocompleteReady(true);
        setGoogleAutocompleteError('');
      }
    };

    if (window.google?.maps?.places) {
      markReady();
      return;
    }

    const existingScript = document.getElementById(USER_GOOGLE_MAPS_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', markReady);
      existingScript.addEventListener('error', () => {
        setGoogleAutocompleteError('Address suggestions are temporarily unavailable.');
      });

      return () => {
        existingScript.removeEventListener('load', markReady);
      };
    }

    const script = document.createElement('script');
    script.id = USER_GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', markReady);
    script.addEventListener('error', () => {
      setGoogleAutocompleteError('Address suggestions are temporarily unavailable.');
    });
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', markReady);
    };
  }, []);

  useEffect(() => {
    if (!googleAutocompleteReady || !addressInputRef.current || !window.google?.maps?.places) return;
    if (autocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['geocode'],
      componentRestrictions: { country: 'gh' },
    });

    autocomplete.setFields(['formatted_address', 'place_id', 'geometry', 'address_components']);
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const cityComponent = (place.address_components || []).find((item) =>
        item.types?.includes('locality') || item.types?.includes('administrative_area_level_2')
      );
      const regionComponent = (place.address_components || []).find((item) =>
        item.types?.includes('administrative_area_level_1')
      );

      setForm((prev) => ({
        ...prev,
        addressLine: place.formatted_address || prev.addressLine,
        addressResolved: place.formatted_address || prev.addressResolved,
        addressPlaceId: place.place_id || '',
        addressLat:
          typeof place.geometry?.location?.lat === 'function'
            ? String(place.geometry.location.lat())
            : prev.addressLat,
        addressLng:
          typeof place.geometry?.location?.lng === 'function'
            ? String(place.geometry.location.lng())
            : prev.addressLng,
        city: cityComponent?.long_name || prev.city,
        region: regionComponent?.long_name || prev.region,
      }));
    });

    placeChangedListenerRef.current = listener;

    return () => {
      if (placeChangedListenerRef.current) {
        window.google.maps.event.removeListener(placeChangedListenerRef.current);
        placeChangedListenerRef.current = null;
      }
    };
  }, [googleAutocompleteReady]);

  const updateField = (key, value) => {
    if (key === 'addressGpsCode') {
      setGhanaPostError('');
      setGhanaPostSuccess('');
    }

    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resolveGhanaPostAddress = useCallback(async (gpsCode) => {
    const normalizedGps = normalizeGhanaPostCode(gpsCode);

    if (!normalizedGps) {
      setGhanaPostError('');
      setGhanaPostSuccess('');
      return;
    }

    const requestId = ghanapostRequestRef.current + 1;
    ghanapostRequestRef.current = requestId;

    try {
      setGhanaPostResolving(true);
      setGhanaPostError('');
      setGhanaPostSuccess('');

      const response = await fetch(GHANAPOST_RESOLVE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: normalizedGps }),
      });

      if (!response.ok) {
        throw new Error('We could not confirm this digital address right now. Please try again.');
      }

      const text = await response.text();
      let payload = {};

      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error('We could not confirm this digital address right now. Please try again.');
      }

      const row = parseGhanaPostRow(payload);
      if (!row) {
        throw new Error('We could not find that digital address. Please check the code and try again.');
      }

      const lat = row.CenterLatitude ?? row.NorthLat ?? row.SouthLat;
      const lng = row.CenterLongitude ?? row.EastLong ?? row.WestLong;
      const resolvedAddress = buildGhanaPostAddress(row);
      const canonicalGpsCode = row.GPSName || row.AddressV1 || normalizedGps;

      if (ghanapostRequestRef.current !== requestId) return;

      setForm((prev) => ({
        ...prev,
        addressGpsCode: canonicalGpsCode,
        addressResolved: resolvedAddress,
        addressLat: lat != null ? String(lat) : prev.addressLat,
        addressLng: lng != null ? String(lng) : prev.addressLng,
      }));
      setGhanaPostSuccess('Address confirmed successfully.');
    } catch (error) {
      if (ghanapostRequestRef.current !== requestId) return;
      setGhanaPostError(error.message || 'We could not confirm this digital address right now.');
    } finally {
      if (ghanapostRequestRef.current === requestId) {
        setGhanaPostResolving(false);
      }
    }
  }, []);

  useEffect(() => {
    const normalized = normalizeGhanaPostCode(form.addressGpsCode || '');

    if (ghanapostDebounceRef.current) {
      window.clearTimeout(ghanapostDebounceRef.current);
      ghanapostDebounceRef.current = null;
    }

    if (!normalized) {
      setGhanaPostError('');
      setGhanaPostSuccess('');
      return;
    }

    if (normalized.length < 7) {
      setGhanaPostSuccess('');
      return;
    }

    ghanapostDebounceRef.current = window.setTimeout(() => {
      resolveGhanaPostAddress(normalized);
    }, 700);

    return () => {
      if (ghanapostDebounceRef.current) {
        window.clearTimeout(ghanapostDebounceRef.current);
        ghanapostDebounceRef.current = null;
      }
    };
  }, [form.addressGpsCode, resolveGhanaPostAddress]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveSettings(form);
    setMessage('Settings saved successfully.');
    window.setTimeout(() => setMessage(''), 2000);
  };

  const handleDeleteAccount = async (event) => {
    event.preventDefault();
    setDeleteError('');
    setDeleteSuccess('');

    if (!deleteReason) {
      setDeleteError('Please select a reason for deleting your account.');
      return;
    }

    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm account removal.');
      return;
    }

    setDeleteSubmitting(true);
    try {
      await onDeleteAccount({
        reason: deleteReason,
        details: deleteDetails.trim(),
      });
      setDeleteSuccess('Your account has been deleted successfully.');
    } catch (error) {
      setDeleteError(error.message || 'Unable to delete account right now.');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div>
      <HeaderBar cartSummary={cartSummary} />

      <div className="breadcrumbs-row">
        <Link to="/">Shop</Link>
        <span>/</span>
        <strong>Settings</strong>
      </div>

      <section className="user-settings-layout user-settings-layout-redesign">
        <article className="user-settings-main-card">
          <div className="user-settings-header user-settings-header-redesign">
            <p className="user-settings-kicker">Account</p>
            <h2>User Settings</h2>
            <p>Personalize your profile, delivery address, and notification preferences in one place.</p>
            <div className="user-settings-quick-stats">
              <div>
                <span>Region</span>
                <strong>{form.region || 'Greater Accra'}</strong>
              </div>
              <div>
                <span>Delivery</span>
                <strong>{form.preferredDelivery === 'express' ? 'Express' : 'Standard'}</strong>
              </div>
              <div>
                <span>Contact</span>
                <strong>{form.preferredContact === 'email' ? 'Email' : 'Phone'}</strong>
              </div>
            </div>
          </div>

          {message && <div className="status-banner success">{message}</div>}

          <form className="user-settings-form" onSubmit={handleSubmit}>
            <div className="user-settings-group user-settings-section-card">
              <h3>Profile Details</h3>
              <div className="user-settings-grid">
                <label>
                  Full Name
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(event) => updateField('fullName', event.target.value)}
                    placeholder="Your full name"
                  />
                </label>

                <label>
                  Phone Number
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(event) => updateField('phone', event.target.value)}
                    placeholder="e.g. +233XXXXXXXXX"
                  />
                </label>
              </div>
            </div>

            <div className="user-settings-group user-settings-section-card">
              <h3>Delivery Preferences</h3>
              <div className="user-settings-grid">
                <label className="user-settings-field-wide">
                  Digital Address (GhanaPost)
                  <div className="user-settings-inline-actions">
                    <input
                      type="text"
                      value={form.addressGpsCode}
                      onChange={(event) => updateField('addressGpsCode', event.target.value)}
                      placeholder="e.g. GA-183-8164"
                      maxLength={24}
                    />
                    <button
                      type="button"
                      className="user-settings-inline-btn"
                      onClick={() => resolveGhanaPostAddress(form.addressGpsCode)}
                      disabled={ghanaPostResolving || !form.addressGpsCode.trim()}
                    >
                      {ghanaPostResolving ? 'Checking...' : 'Check'}
                    </button>
                  </div>
                  <small className="user-settings-help-note">
                    Enter your GhanaPost digital address and we will confirm it automatically.
                  </small>
                  {ghanaPostSuccess && <small className="user-settings-help-note success">{ghanaPostSuccess}</small>}
                  {ghanaPostError && <small className="user-settings-help-note error">{ghanaPostError}</small>}
                </label>

                <label className="user-settings-field-wide">
                  Address
                  <input
                    ref={addressInputRef}
                    type="text"
                    value={form.addressLine}
                    onChange={(event) => updateField('addressLine', event.target.value)}
                    placeholder="e.g. 24 Ring Road, Accra, Ghana"
                    maxLength={260}
                  />
                  {googleAutocompleteReady && (
                    <small className="user-settings-help-note">
                      Start typing and choose a suggested address.
                    </small>
                  )}
                  {googleAutocompleteError && <small className="user-settings-help-note error">{googleAutocompleteError}</small>}
                </label>

                <label className="user-settings-field-wide">
                  Confirmed Address
                  <input
                    type="text"
                    value={form.addressResolved}
                    readOnly
                    placeholder="Your confirmed address will appear here"
                  />
                </label>

                <label>
                  City / Town
                  <input
                    type="text"
                    value={form.city}
                    onChange={(event) => updateField('city', event.target.value)}
                    placeholder="e.g. Accra"
                  />
                </label>

                <label>
                  Region
                  <select
                    value={form.region}
                    onChange={(event) => updateField('region', event.target.value)}
                  >
                    <option value="Greater Accra">Greater Accra</option>
                    <option value="Ashanti">Ashanti</option>
                    <option value="Western">Western</option>
                    <option value="Central">Central</option>
                    <option value="Eastern">Eastern</option>
                    <option value="Northern">Northern</option>
                  </select>
                </label>

                <label>
                  Landmark
                  <input
                    type="text"
                    value={form.landmark}
                    onChange={(event) => updateField('landmark', event.target.value)}
                    placeholder="Optional delivery landmark"
                  />
                </label>

                <div className="user-settings-location-metrics">
                  <div>
                    <span>Digital Address</span>
                    <strong>{form.addressGpsCode || 'Not set'}</strong>
                  </div>
                  <div>
                    <span>Town/City</span>
                    <strong>{form.city || 'Not set'}</strong>
                  </div>
                  <div>
                    <span>Region</span>
                    <strong>{form.region || 'Not set'}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="user-settings-group user-settings-section-card">
              <h3>Notifications</h3>
              <label className="user-settings-toggle-row">
                <input
                  type="checkbox"
                  checked={form.orderAlerts}
                  onChange={(event) => updateField('orderAlerts', event.target.checked)}
                />
                <span>
                  <strong>Order Alerts</strong>
                  <small>Receive updates for order status and delivery progress.</small>
                </span>
              </label>

              <label className="user-settings-toggle-row">
                <input
                  type="checkbox"
                  checked={form.emailUpdates}
                  onChange={(event) => updateField('emailUpdates', event.target.checked)}
                />
                <span>
                  <strong>Email Updates</strong>
                  <small>Get account and platform updates by email.</small>
                </span>
              </label>

            </div>

            <div className="user-settings-actions">
              <button type="submit" className="checkout-btn user-settings-save-btn">Save Settings</button>
            </div>
          </form>

          <section className="user-settings-delete-card" aria-labelledby="delete-account-heading">
            <div className="user-settings-delete-header">
              <p className="user-settings-delete-kicker">Danger zone</p>
              <h3 id="delete-account-heading">Delete Account</h3>
              <p>
                This action is permanent. Your account access will be removed immediately.
              </p>
            </div>

            {deleteError && <div className="status-banner">{deleteError}</div>}
            {deleteSuccess && <div className="status-banner success">{deleteSuccess}</div>}

            <form className="user-settings-delete-form" onSubmit={handleDeleteAccount}>
              <label>
                Why are you leaving?
                <select
                  value={deleteReason}
                  onChange={(event) => setDeleteReason(event.target.value)}
                  required
                >
                  <option value="">Select a reason</option>
                  <option value="privacy_concerns">Privacy concerns</option>
                  <option value="not_using_anymore">I no longer use this account</option>
                  <option value="too_many_notifications">Too many notifications</option>
                  <option value="found_alternative">I found a better alternative</option>
                  <option value="poor_experience">Poor shopping experience</option>
                  <option value="other">Other</option>
                </select>
              </label>

              <label>
                Additional feedback (optional)
                <textarea
                  value={deleteDetails}
                  onChange={(event) => setDeleteDetails(event.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder="Share any details that can help us improve."
                />
              </label>

              <label>
                Type DELETE to confirm
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(event) => setDeleteConfirmText(event.target.value)}
                  placeholder="DELETE"
                  required
                />
              </label>

              <button type="submit" className="user-settings-delete-btn" disabled={deleteSubmitting}>
                {deleteSubmitting ? 'Deleting account...' : 'Delete My Account'}
              </button>
            </form>
          </section>
        </article>

        <aside className="user-settings-side-card user-settings-side-card-redesign">
          <h3>Account Summary</h3>
          <div className="user-settings-summary-list">
            <p><span>Name:</span> <strong>{userSession?.user?.fullName || 'User'}</strong></p>
            <p><span>Email:</span> <strong>{userSession?.user?.email || 'Not added'}</strong></p>
            <p><span>Phone:</span> <strong>{form.phone || userSession?.user?.phone || 'Not added'}</strong></p>
            <p><span>Status:</span> <strong>{userSession?.user?.isSuspended ? 'Suspended' : 'Active'}</strong></p>
          </div>
          <p className="mini-note">Your changes are saved for this account on this device.</p>
        </aside>
      </section>
    </div>
  );
};

const ImageLightbox = ({ image, onClose }) => {
  useEffect(() => {
    if (!image) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [image, onClose]);

  if (!image) return null;

  return (
    <div className="image-lightbox" role="dialog" aria-modal="true" aria-label="Full product image" onClick={onClose}>
      <button type="button" className="image-lightbox-close" onClick={onClose} aria-label="Close full image view">
        ×
      </button>
      <img
        src={image.src}
        alt={image.alt || 'Product image'}
        className="image-lightbox-content"
        onClick={(event) => event.stopPropagation()}
      />
    </div>
  );
};

const AuthPage = ({ mode, userSession, onAuthSuccess, onNotify }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectTarget = params.get('redirect') || '/';
  const isLogin = mode === 'login';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const googleButtonRef = useRef(null);

  useEffect(() => {
    if (userSession?.user) {
      navigate(redirectTarget, { replace: true });
    }
  }, [userSession, navigate, redirectTarget]);

  const handleGoogleCredential = useCallback(async (response) => {
    const credential = response?.credential;
    if (!credential) {
      setError('Google sign-in was cancelled. Please try again.');
      return;
    }

    setError('');
    setGoogleSubmitting(true);

    try {
      const payload = await googleAuthUserRequest(credential);
      onAuthSuccess({ token: payload.token, user: payload.user });
      onNotify?.('Signed in with Google successfully.', 'success');
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      const message = err.message || 'Google sign-in failed. Please try again.';
      setError(message);
      onNotify?.(message, 'error');
    } finally {
      setGoogleSubmitting(false);
    }
  }, [navigate, onAuthSuccess, onNotify, redirectTarget]);

  useEffect(() => {
    if (!googleButtonRef.current) {
      return undefined;
    }

    let cancelled = false;

    const renderGoogleButton = () => {
      if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current || !GOOGLE_CLIENT_ID) {
        return;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });

      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        shape: 'pill',
        size: 'large',
        theme: 'outline',
        text: isLogin ? 'signin_with' : 'signup_with',
        width: 320,
      });

      setGoogleReady(true);
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return () => {
        cancelled = true;
      };
    }

    const existingScript = document.getElementById('tk-google-identity-script');
    if (existingScript) {
      existingScript.addEventListener('load', renderGoogleButton, { once: true });
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement('script');
    script.id = 'tk-google-identity-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [handleGoogleCredential, isLogin]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError('Email is required.');
      return;
    }

    if (!password) {
      setError('Password is required.');
      return;
    }

    if (!isLogin) {
      if (fullName.trim().length < 2) {
        setError('Please enter your full name.');
        return;
      }

      if (!/^\+?[0-9]{10,15}$/.test(phone.trim())) {
        setError('Please enter a valid phone number (10-15 digits, optional + prefix).');
        return;
      }

      if (!isStrongUserPassword(password)) {
        setError('Password must include 8+ chars, uppercase, lowercase, number, and one symbol from @ # $ % &.');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = isLogin
        ? await loginUserRequest({ email: normalizedEmail, password })
        : await registerUserRequest({
          fullName: fullName.trim(),
          email: normalizedEmail,
          phone: phone.trim(),
          password,
        });

      onAuthSuccess({ token: payload.token, user: payload.user });
      onNotify?.(isLogin ? 'Signed in successfully.' : 'Account created successfully.', 'success');
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
      onNotify?.(err.message || 'Authentication failed. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="auth-page-shell">
      <div className="auth-card">
        <p className="auth-kicker">TK Fashion & Design</p>
        <h2>{isLogin ? 'Welcome Back' : 'Create Your Account'}</h2>
        <p className="auth-subtitle">
          {isLogin
            ? 'Sign in to continue to secure payment and order tracking.'
            : 'Register to save your details and continue to checkout securely.'}
        </p>

        {error && <div className="status-banner">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {!isLogin && (
            <label>
              Full Name
              <input
                type="text"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                placeholder="e.g. Akosua Mensah"
                required
              />
            </label>
          )}

          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              required
            />
          </label>

          {!isLogin && (
            <label>
              Phone Number
              <input
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="+233XXXXXXXXX"
                required
              />
            </label>
          )}

          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={isLogin ? 'Enter your password' : 'At least 8 chars with @ # $ % &'}
              required
            />
          </label>

          {!isLogin && (
            <label>
              Confirm Password
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Re-enter password"
                required
              />
            </label>
          )}

          {!isLogin && (
            <p className="auth-password-hint">
              Password must include at least 8 characters, one uppercase letter, one lowercase letter,
              one number, and one symbol from @ # $ % &.
            </p>
          )}

          <button type="submit" className="checkout-btn" disabled={submitting}>
            {submitting
              ? (isLogin ? 'Signing In...' : 'Creating Account...')
              : (isLogin ? 'Login' : 'Register')}
          </button>
        </form>

        <>
          <div className="auth-divider" role="presentation">
            <span>or</span>
          </div>

          <div className="auth-google-section">
            <div
              className={`auth-google-button-wrap ${googleSubmitting ? 'is-busy' : ''}`}
              ref={googleButtonRef}
            />
            {!googleReady && !googleSubmitting && (
              <p className="auth-google-hint">Loading Google sign-in...</p>
            )}
            {googleSubmitting && (
              <p className="auth-google-hint">Please wait while we sign you in with Google.</p>
            )}
          </div>
        </>

        <p className="auth-switch-text">
          {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
          <Link to={`${isLogin ? '/register' : '/login'}?redirect=${encodeURIComponent(redirectTarget)}`}>
            {isLogin ? 'Register here' : 'Login here'}
          </Link>
        </p>

        {isLogin && (
          <p className="auth-switch-text">
            Forgot your password?{' '}
            <Link to="/forgot-password">
              Reset it here
            </Link>
          </p>
        )}

        {isLogin && (
          <p className="auth-switch-text">
            Policies:{' '}
            <Link to="/policies/acceptance">Acceptance Policy</Link>
            {' '}·{' '}
            <Link to="/policies/terms">Terms &amp; Conditions</Link>
            {' '}·{' '}
            <Link to="/policies/privacy">Privacy Policy</Link>
          </p>
        )}
      </div>
    </section>
  );
};

const ShopPage = ({
  products,
  loading,
  error,
  searchTerm,
  setSearchTerm,
  activeCategory,
  setActiveCategory,
  quantityDrafts,
  setQuantityDrafts,
  quickAddToCart,
  mediaViews,
  setMediaViews,
  onOpenImage,
}) => {
  const categories = useMemo(() => {
    const values = products
      .map((item) => item.category)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    return ['All', ...new Set(values)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return products.filter((item) => {
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      if (!matchesCategory) return false;
      if (!term) return true;

      const haystack = [item.name, item.category, item.subCategory, item.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [products, searchTerm, activeCategory]);


  // ── Responsive Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => (window.innerWidth > 900 ? 100 : 50));

  // Update itemsPerPage on resize
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(window.innerWidth > 900 ? 100 : 50);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset to page 1 when filters or itemsPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeCategory, itemsPerPage]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const setMediaView = (productId, patch) => {
    setMediaViews((prev) => ({
      ...prev,
      [productId]: {
        type: 'image',
        index: 0,
        ...(prev[productId] || {}),
        ...patch,
      },
    }));
  };

  const getMediaView = (product) => {
    const existing = mediaViews[product._id];
    if (existing) return existing;
    return {
      type: (product.images || []).length > 0 ? 'image' : 'video',
      index: 0,
    };
  };

  const switchMediaType = (product, nextType) => {
    if (getMediaItems(product, nextType).length === 0) return;
    setMediaView(product._id, { type: nextType, index: 0 });
  };

  const navigateMedia = (product, direction) => {
    const current = getMediaView(product);
    const items = getMediaItems(product, current.type);
    if (items.length <= 1) return;

    const nextIndex =
      direction === 'next'
        ? (current.index + 1) % items.length
        : (current.index - 1 + items.length) % items.length;

    setMediaView(product._id, { index: nextIndex });
  };

  useEffect(() => {
    const timerId = setInterval(() => {
      setMediaViews((prev) => {
        let changed = false;
        let next = prev;

        filteredProducts.forEach((product) => {
          const view = prev[product._id] || { type: 'image', index: 0 };
          const images = getMediaItems(product, 'image');

          if (view.type !== 'image' || images.length <= 1) return;

          const nextIndex = (view.index + 1) % images.length;

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          next[product._id] = { ...view, index: nextIndex };
        });

        return changed ? next : prev;
      });
    }, 10000);

    return () => clearInterval(timerId);
  }, [filteredProducts, setMediaViews]);

  return (
    <>
      <div className="shop-controls">
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search by name, category, or style"
          className="shop-search"
        />
      </div>

      <section className="shop-toolbar" aria-label="Category filters">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            className={`category-chip ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </section>

      {loading && (
        <div className="shop-state-card">
          <h3>Loading inventory...</h3>
          <p>Hold on while we pull the latest stock.</p>
        </div>
      )}

      {!loading && error && (
        <div className="shop-state-card error">
          <h3>Could not load products</h3>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && filteredProducts.length === 0 && (
        <div className="shop-state-card">
          <h3>No products found</h3>
          <p>Try changing your search or category filter.</p>
        </div>
      )}

      {!loading && !error && filteredProducts.length > 0 && (
        <>
          <main className="products-grid" aria-label="Available stock">
            {paginatedProducts.map((product) => {
              const view = getMediaView(product);
              const imageItems = getMediaItems(product, 'image');
              const videoItems = getMediaItems(product, 'video');

              const hasImages = imageItems.length > 0;
              const hasVideos = videoItems.length > 0;
              const activeType = view.type === 'video' && hasVideos ? 'video' : 'image';
              const activeItems = activeType === 'video' ? videoItems : imageItems;
              const activeIndex = activeItems.length > 0 ? Math.min(view.index, activeItems.length - 1) : 0;
              const activeUrl = activeItems[activeIndex] || '';

              const stock = Number(product.quantity || 0);
              const quickQty = quantityDrafts[product._id] || 1;
              const quickPrice = getPricingForSelection(product, {
                ...getDefaultSelection(product),
                quantity: quickQty,
              }).unitPrice;

              return (
                <article key={product._id} className="product-card">
                  <div className="product-media">
                    <div className="media-type-switch" role="tablist" aria-label="Switch product media">
                      <button
                        type="button"
                        className={`media-type-btn ${activeType === 'image' ? 'active' : ''}`}
                        onClick={() => switchMediaType(product, 'image')}
                        disabled={!hasImages}
                      >
                        Images
                      </button>
                      <button
                        type="button"
                        className={`media-type-btn ${activeType === 'video' ? 'active' : ''}`}
                        onClick={() => switchMediaType(product, 'video')}
                        disabled={!hasVideos}
                      >
                        Videos
                      </button>
                    </div>

                    {activeUrl ? (
                      activeType === 'video' ? (
                        <video src={activeUrl} controls preload="metadata" className="product-media-video" />
                      ) : (
                        <button
                          type="button"
                          className="product-media-image-btn"
                          onClick={() => onOpenImage({ src: activeUrl, alt: product.name })}
                          aria-label={`View full image of ${product.name}`}
                        >
                          <img src={activeUrl} alt={product.name} />
                        </button>
                      )
                    ) : (
                      <div className="product-media-placeholder">No Media</div>
                    )}

                    {activeItems.length > 1 && (
                      <div className="media-carousel-controls">
                        <button
                          type="button"
                          className="media-nav-btn"
                          onClick={() => navigateMedia(product, 'prev')}
                          aria-label="Previous media"
                        >
                          ‹
                        </button>
                        <span className="media-index-label">
                          {activeIndex + 1} / {activeItems.length}
                        </span>
                        <button
                          type="button"
                          className="media-nav-btn"
                          onClick={() => navigateMedia(product, 'next')}
                          aria-label="Next media"
                        >
                          ›
                        </button>
                      </div>
                    )}

                    <span className={`stock-badge ${stock > 0 ? 'in' : 'out'}`}>
                      {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                    </span>
                  </div>

                  <div className="product-content">
                    <p className="product-meta">{product.category} • {product.subCategory}</p>
                    <Link to={`/product/${product._id}`} className="product-title-link">
                      <h2>{product.name}</h2>
                    </Link>
                    <p className="product-price">{formatCurrency(quickPrice)}</p>

                    <div className="variant-row">
                      <span>{(product.sizes || []).length} sizes</span>
                      <span>{(product.colors || []).length} colors</span>
                    </div>

                    <div className="quick-add-row">
                      <label htmlFor={`quick-qty-${product._id}`}>Qty</label>
                      <input
                        id={`quick-qty-${product._id}`}
                        type="number"
                        min="1"
                        max={Math.max(1, stock)}
                        value={quickQty}
                        onChange={(event) => {
                          const nextQty = Math.max(1, Number(event.target.value || 1));
                          setQuantityDrafts((prev) => ({ ...prev, [product._id]: nextQty }));
                        }}
                      />
                      <button
                        type="button"
                        className="add-cart-btn"
                        onClick={() => quickAddToCart(product, quickQty)}
                        disabled={stock < 1}
                      >
                        Add to Cart
                      </button>
                    </div>

                    <Link to={`/product/${product._id}`} className="details-btn details-link-btn">
                      View Product Details
                    </Link>
                  </div>
                </article>
              );
            })}
          </main>

          <div className="shop-pagination" aria-label="Product pagination">
            <button
              type="button"
              className="pagination-btn"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>

            <div className="pagination-info">
              Page {currentPage} of {totalPages} ({filteredProducts.length} items)
            </div>

            <button
              type="button"
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </>
  );
};

const ProductDetailsPage = ({ products, cartItems, upsertCartItem, cartSummary, onOpenImage }) => {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const cartKey = new URLSearchParams(location.search).get('cartKey') || '';

  const product = useMemo(
    () => products.find((item) => String(item._id) === String(productId)),
    [products, productId]
  );

  const existingCartItem = useMemo(
    () => cartItems.find((item) => item.key === cartKey),
    [cartItems, cartKey]
  );

  const [selection, setSelection] = useState({ size: '', color: '', quantity: 1 });
  const [localMessage, setLocalMessage] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [mediaIndex, setMediaIndex] = useState(0);

  // ── Dynamic product SEO
  const productSEO = useMemo(() => buildProductSEO(product), [product]);
  useSEO(productSEO);

  useEffect(() => {
    if (!product) return;

    if (existingCartItem) {
      setSelection({
        size: existingCartItem.size || '',
        color: existingCartItem.color || '',
        quantity: existingCartItem.quantity || 1,
      });
    } else {
      setSelection(getDefaultSelection(product));
    }

    setMediaType((product.images || []).length > 0 ? 'image' : 'video');
    setMediaIndex(0);
  }, [product, existingCartItem]);

  if (!product) {
    return (
      <div className="shop-state-card">
        <h3>Product not found</h3>
        <p>This product may have been removed from stock.</p>
        <button type="button" className="details-btn" onClick={() => navigate('/')}>Back to Shop</button>
      </div>
    );
  }

  const imageItems = getMediaItems(product, 'image');
  const videoItems = getMediaItems(product, 'video');
  const activeItems = mediaType === 'video' ? videoItems : imageItems;
  const safeIndex = activeItems.length > 0 ? Math.min(mediaIndex, activeItems.length - 1) : 0;
  const activeUrl = activeItems[safeIndex] || '';

  const selectedPricing = getPricingForSelection(product, selection);
  const selectedPrice = selectedPricing.unitPrice;
  const priceRows = getDetailedPrices(product);

  const saveCartChange = () => {
    if (product.pricing?.mode === 'per-size' && !selection.size) {
      setLocalMessage('Please select a size before adding to cart.');
      return;
    }

    if (product.pricing?.mode === 'per-color' && !selection.color) {
      setLocalMessage('Please select a color before adding to cart.');
      return;
    }

    if (selection.quantity < 1) {
      setLocalMessage('Quantity must be at least 1.');
      return;
    }

    upsertCartItem(product, selection, existingCartItem?.key || null);
    navigate('/cart');
  };

  return (
    <div>
      <HeaderBar cartSummary={cartSummary} />

      <div className="breadcrumbs-row">
        <Link to="/">Shop</Link>
        <span>/</span>
        <Link to="/cart">Cart</Link>
        <span>/</span>
        <strong>{product.name}</strong>
      </div>

      <section className="details-layout">
        <article className="details-media-card">
          <div className="details-media-topbar">
            <div>
              <p className="details-media-label">Media Gallery</p>
              <h3 className="details-media-title">Preview This Product</h3>
            </div>

            <div className="details-media-switch" role="tablist" aria-label="Switch product media">
              <button
                type="button"
                className={`media-type-btn ${mediaType === 'image' ? 'active' : ''}`}
                onClick={() => {
                  if (imageItems.length > 0) {
                    setMediaType('image');
                    setMediaIndex(0);
                  }
                }}
                disabled={imageItems.length === 0}
              >
                Images
              </button>
              <button
                type="button"
                className={`media-type-btn ${mediaType === 'video' ? 'active' : ''}`}
                onClick={() => {
                  if (videoItems.length > 0) {
                    setMediaType('video');
                    setMediaIndex(0);
                  }
                }}
                disabled={videoItems.length === 0}
              >
                Videos
              </button>
            </div>
          </div>

          <div className="details-main-media">
            {activeUrl ? (
              mediaType === 'video' ? (
                <video src={activeUrl} controls preload="metadata" className="product-media-video" />
              ) : (
                <button
                  type="button"
                  className="details-main-image-btn"
                  onClick={() => onOpenImage({ src: activeUrl, alt: product.name })}
                  aria-label={`View full image of ${product.name}`}
                >
                  <img src={activeUrl} alt={product.name} />
                </button>
              )
            ) : (
              <div className="product-media-placeholder">No Media</div>
            )}

            <div className="details-media-floating-meta">
              <span className="details-media-type-pill">{mediaType === 'video' ? 'Video' : 'Image'}</span>
              <span className="details-media-count-pill">{safeIndex + 1} / {Math.max(activeItems.length, 1)}</span>
            </div>

            {activeItems.length > 1 && (
              <>
                <button
                  type="button"
                  className="details-media-arrow left"
                  onClick={() => setMediaIndex((value) => (value - 1 + activeItems.length) % activeItems.length)}
                  aria-label="Previous media"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="details-media-arrow right"
                  onClick={() => setMediaIndex((value) => (value + 1) % activeItems.length)}
                  aria-label="Next media"
                >
                  ›
                </button>
              </>
            )}
          </div>

          {activeItems.length > 1 && (
            <div className="media-carousel-controls details-carousel-controls">
              <button
                type="button"
                className="media-nav-btn"
                onClick={() => setMediaIndex((value) => (value - 1 + activeItems.length) % activeItems.length)}
                aria-label="Previous media"
              >
                ‹
              </button>
              <span className="media-index-label">
                {safeIndex + 1} / {activeItems.length}
              </span>
              <button
                type="button"
                className="media-nav-btn"
                onClick={() => setMediaIndex((value) => (value + 1) % activeItems.length)}
                aria-label="Next media"
              >
                ›
              </button>
            </div>
          )}

          {activeItems.length > 1 && (
            <div className="details-media-thumbnails" aria-label="Media thumbnails">
              {activeItems.map((itemUrl, index) => (
                <button
                  key={`${mediaType}-${itemUrl}-${index}`}
                  type="button"
                  className={`details-thumbnail ${safeIndex === index ? 'active' : ''}`}
                  onClick={() => setMediaIndex(index)}
                  aria-label={`View ${mediaType} ${index + 1}`}
                >
                  {mediaType === 'video' ? (
                    <div className="details-thumbnail-video">Video {index + 1}</div>
                  ) : (
                    <img src={itemUrl} alt={`${product.name} ${index + 1}`} />
                  )}
                </button>
              ))}
            </div>
          )}
        </article>

        <article className="details-info-card">
          <p className="product-meta">{product.category} • {product.subCategory}</p>
          <h2 className="details-title">{product.name}</h2>
          <p className="details-price">{formatCurrency(selectedPrice)}</p>

          {product.description && <p className="details-description">{product.description}</p>}

          <div className="details-block">
            <h3>Product Variants</h3>

            {(product.sizes || []).length > 0 && (
              <div className="selector-group">
                <label>Choose Size</label>
                <div className="chip-list">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      className={`variant-chip ${selection.size === size ? 'active' : ''}`}
                      onClick={() => setSelection((prev) => ({ ...prev, size }))}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(product.colors || []).length > 0 && (
              <div className="selector-group">
                <label>Choose Color</label>
                <div className="chip-list">
                  {product.colors.map((color) => (
                    <button
                      key={color.name}
                      type="button"
                      className={`variant-chip ${selection.color === color.name ? 'active' : ''}`}
                      onClick={() => setSelection((prev) => ({ ...prev, color: color.name }))}
                    >
                      <span className="color-dot-inline" style={{ backgroundColor: color.hex }} />
                      {color.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="selector-group">
              <label htmlFor="details-qty">Quantity</label>
              <input
                id="details-qty"
                type="number"
                min="1"
                max={Math.max(1, Number(product.quantity || 0))}
                value={selection.quantity}
                onChange={(event) => {
                  const nextQty = Math.max(1, Number(event.target.value || 1));
                  setSelection((prev) => ({ ...prev, quantity: nextQty }));
                }}
                className="qty-input"
              />
            </div>
          </div>

          <div className="details-block">
            <h3>Pricing Overview</h3>
            <p className="pricing-strategy-intro">
              Prices can be the same for all options, or set by size or color, with optional bulk savings.
            </p>
            {priceRows.length > 0 ? (
              <ul className="price-breakdown-list">
                {priceRows.map((row) => (
                  <li key={row.label}>
                    <span>{row.label}</span>
                    <strong>{formatCurrency(row.value)}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mini-note">No explicit pricing data available.</p>
            )}

            <p className="mini-note">
              Your current selection: {selectedPricing.note}
              {typeof selectedPricing.unitPrice === 'number' ? ` (${formatCurrency(selectedPricing.unitPrice)} each)` : ''}
            </p>
          </div>

          <div className="details-block">
            <h3>Delivery Options</h3>
            <ul className="delivery-list">
              {product.delivery?.standard?.enabled && (
                <li>Standard: {product.delivery.standard.days || 'Available'}</li>
              )}
              {product.delivery?.express?.enabled && (
                <li>Express: {product.delivery.express.days || 'Available'}</li>
              )}
              {!product.delivery?.standard?.enabled && !product.delivery?.express?.enabled && (
                <li>Delivery details not available.</li>
              )}
            </ul>
          </div>

          {localMessage && <div className="status-banner">{localMessage}</div>}

          <div className="details-actions-row">
            <button type="button" className="details-btn" onClick={() => navigate('/cart')}>
              Back to Cart
            </button>
            <button type="button" className="add-cart-btn" onClick={saveCartChange}>
              {existingCartItem ? 'Update Cart Item' : 'Add To Cart'}
            </button>
          </div>
        </article>
      </section>
    </div>
  );
};

const CartPage = ({ cartItems, cartSummary, updateCartQuantity, removeCartItem }) => {
  return (
    <div>
      <HeaderBar cartSummary={cartSummary} />

      <div className="breadcrumbs-row">
        <Link to="/">Shop</Link>
        <span>/</span>
        <strong>Cart</strong>
      </div>

      <section className="cart-page-layout">
        <article className="cart-section">
          <h2>Cart Items</h2>

          {cartItems.length === 0 ? (
            <div className="shop-state-card">
              <h3>Your cart is empty</h3>
              <p>Select products and add them to cart.</p>
              <Link to="/" className="details-btn details-link-btn">Continue Shopping</Link>
            </div>
          ) : (
            <ul className="cart-list">
              {cartItems.map((item) => (
                <li key={item.key} className="cart-item">
                  {item.image ? (
                    <img src={item.image} alt={item.name} />
                  ) : (
                    <div className="cart-image-placeholder">No Image</div>
                  )}

                  <div className="cart-item-content">
                    <Link to={`/product/${item.productId}?cartKey=${encodeURIComponent(item.key)}`} className="cart-item-title-link">
                      <p>{item.name}</p>
                    </Link>
                    <span>{item.category}</span>
                    <small>
                      {item.size ? `Size: ${item.size}` : 'No size'} • {item.color ? `Color: ${item.color}` : 'No color'}
                    </small>
                    {item.pricingNote && <small className="cart-pricing-note">Applied: {item.pricingNote}</small>}

                    <div className="cart-qty-row">
                      <button
                        type="button"
                        onClick={() => updateCartQuantity(item.key, Math.max(1, item.quantity - 1))}
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(event) => {
                          const next = Math.max(1, Number(event.target.value || 1));
                          updateCartQuantity(item.key, next);
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => updateCartQuantity(item.key, item.quantity + 1)}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <strong>{formatCurrency((item.price || 0) * item.quantity)}</strong>
                  </div>

                  <button type="button" className="remove-cart-btn" onClick={() => removeCartItem(item.key)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </article>

        <aside className="cart-summary-card">
          <h3>Order Summary</h3>
          <p>Total Items: <strong>{cartSummary.quantity}</strong></p>
          <p>Total Amount: <strong>{formatCurrency(cartSummary.amount)}</strong></p>
          <Link to="/checkout" className={`checkout-btn ${cartItems.length === 0 ? 'disabled' : ''}`}>
            Proceed to Checkout
          </Link>
          <p className="mini-note">Choose product variants on each product details page before checkout.</p>
        </aside>
      </section>
    </div>
  );
};

const CheckoutPage = ({ products, cartItems, cartSummary, userSession, userSettings, onNotify }) => {
  const navigate = useNavigate();
  const productMap = useMemo(
    () => new Map(products.map((product) => [String(product._id), product])),
    [products]
  );

  const [deliveryChoices, setDeliveryChoices] = useState({});
  const [receiveMethod, setReceiveMethod] = useState('delivery');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [googleAutocompleteReady, setGoogleAutocompleteReady] = useState(false);
  const [googleAutocompleteError, setGoogleAutocompleteError] = useState('');
  const [shopPickupInfo, setShopPickupInfo] = useState({
    shopName: '',
    address: '',
    lat: '',
    lng: '',
    regionDeliveryPrices: {},
    cityDeliveryPrices: [],
  });
  const [detectedRegion, setDetectedRegion] = useState('');
  const [checkoutGpsCode, setCheckoutGpsCode] = useState('');
  const [checkoutGhanaPostResolving, setCheckoutGhanaPostResolving] = useState(false);
  const [checkoutGhanaPostError, setCheckoutGhanaPostError] = useState('');
  const [checkoutGhanaPostSuccess, setCheckoutGhanaPostSuccess] = useState('');
  const [paystackRedirecting, setPaystackRedirecting] = useState(false);
  const deliveryAddressInputRef = useRef(null);
  const deliveryAutocompleteRef = useRef(null);
  const deliveryPlaceChangedListenerRef = useRef(null);
  const checkoutGhanapostDebounceRef = useRef(null);
  const checkoutGhanapostRequestRef = useRef(0);

  useEffect(() => {
    setDeliveryChoices((prev) => {
      const next = {};

      cartItems.forEach((item) => {
        const product = productMap.get(String(item.productId));
        const standardEnabled = product?.delivery?.standard?.enabled !== false;
        const expressEnabled = Boolean(product?.delivery?.express?.enabled);

        if (prev[item.key] === 'express' && expressEnabled) {
          next[item.key] = 'express';
          return;
        }

        if (prev[item.key] === 'standard' && standardEnabled) {
          next[item.key] = 'standard';
          return;
        }

        next[item.key] = expressEnabled && !standardEnabled ? 'express' : 'standard';
      });

      const hasChanged =
        Object.keys(next).length !== Object.keys(prev).length ||
        Object.keys(next).some((key) => next[key] !== prev[key]);

      return hasChanged ? next : prev;
    });
  }, [cartItems, productMap]);

  useEffect(() => {
    setRecipientName((userSettings?.fullName || userSession?.user?.fullName || '').trim());
    setRecipientPhone((userSettings?.phone || userSession?.user?.phone || '').trim());
    setDeliveryAddress((userSettings?.addressResolved || userSettings?.addressLine || '').trim());
  }, [userSession, userSettings]);

  useEffect(() => {
    const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || DEFAULT_GOOGLE_MAPS_API_KEY || '')
      .trim()
      .replace(/^"(.*)"$/, '$1')
      .replace(/^'(.*)'$/, '$1');

    if (!apiKey) {
      setGoogleAutocompleteError('Address suggestions are temporarily unavailable.');
      return;
    }

    const markReady = () => {
      if (window.google?.maps?.places) {
        setGoogleAutocompleteReady(true);
        setGoogleAutocompleteError('');
      }
    };

    if (window.google?.maps?.places) {
      markReady();
      return;
    }

    const existingScript = document.getElementById(USER_GOOGLE_MAPS_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', markReady);
      existingScript.addEventListener('error', () => {
        setGoogleAutocompleteError('Address suggestions are temporarily unavailable.');
      });

      return () => {
        existingScript.removeEventListener('load', markReady);
      };
    }

    const script = document.createElement('script');
    script.id = USER_GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', markReady);
    script.addEventListener('error', () => {
      setGoogleAutocompleteError('Address suggestions are temporarily unavailable.');
    });
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', markReady);
    };
  }, []);

  const resolveCheckoutGpsAddress = useCallback(async (gpsCode) => {
    const normalizedGps = normalizeGhanaPostCode(gpsCode);
    if (!normalizedGps) {
      setCheckoutGhanaPostError('');
      setCheckoutGhanaPostSuccess('');
      return;
    }
    const requestId = checkoutGhanapostRequestRef.current + 1;
    checkoutGhanapostRequestRef.current = requestId;
    try {
      setCheckoutGhanaPostResolving(true);
      setCheckoutGhanaPostError('');
      setCheckoutGhanaPostSuccess('');
      const response = await fetch(GHANAPOST_RESOLVE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: normalizedGps }),
      });
      if (!response.ok) throw new Error('We could not confirm this digital address right now. Please try again.');
      const text = await response.text();
      let payload = {};
      try { payload = JSON.parse(text); } catch { throw new Error('We could not confirm this digital address right now. Please try again.'); }
      const row = parseGhanaPostRow(payload);
      if (!row) throw new Error('We could not find that digital address. Please check the code and try again.');
      const resolvedAddress = buildGhanaPostAddress(row);
      const canonicalGpsCode = row.GPSName || row.AddressV1 || normalizedGps;
      if (checkoutGhanapostRequestRef.current !== requestId) return;
      setCheckoutGpsCode(canonicalGpsCode);
      setDeliveryAddress(resolvedAddress);
      if (row.Region) setDetectedRegion(row.Region.trim());
      setCheckoutGhanaPostSuccess('Address confirmed — delivery address updated.');
    } catch (error) {
      if (checkoutGhanapostRequestRef.current !== requestId) return;
      setCheckoutGhanaPostError(error.message || 'We could not confirm this digital address right now.');
    } finally {
      if (checkoutGhanapostRequestRef.current === requestId) setCheckoutGhanaPostResolving(false);
    }
  }, []);

  useEffect(() => {
    const normalized = normalizeGhanaPostCode(checkoutGpsCode || '');
    if (checkoutGhanapostDebounceRef.current) {
      window.clearTimeout(checkoutGhanapostDebounceRef.current);
      checkoutGhanapostDebounceRef.current = null;
    }
    if (!normalized) { setCheckoutGhanaPostError(''); setCheckoutGhanaPostSuccess(''); return; }
    if (normalized.length < 7) { setCheckoutGhanaPostSuccess(''); return; }
    checkoutGhanapostDebounceRef.current = window.setTimeout(() => {
      resolveCheckoutGpsAddress(normalized);
    }, 700);
    return () => {
      if (checkoutGhanapostDebounceRef.current) {
        window.clearTimeout(checkoutGhanapostDebounceRef.current);
        checkoutGhanapostDebounceRef.current = null;
      }
    };
  }, [checkoutGpsCode, resolveCheckoutGpsAddress]);

  useEffect(() => {
    if (!googleAutocompleteReady || !deliveryAddressInputRef.current || !window.google?.maps?.places) return;
    if (deliveryAutocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(deliveryAddressInputRef.current, {
      types: ['geocode'],
      componentRestrictions: { country: 'gh' },
    });

    autocomplete.setFields(['formatted_address', 'address_components']);
    deliveryAutocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      setDeliveryAddress(place.formatted_address || '');
      // Extract administrative_area_level_1 as the Ghana region
      const regionComponent = (place.address_components || []).find(
        (comp) => comp.types.includes('administrative_area_level_1')
      );
      if (regionComponent) setDetectedRegion(regionComponent.long_name.trim());
    });

    deliveryPlaceChangedListenerRef.current = listener;

    return () => {
      if (deliveryPlaceChangedListenerRef.current) {
        window.google.maps.event.removeListener(deliveryPlaceChangedListenerRef.current);
        deliveryPlaceChangedListenerRef.current = null;
      }
    };
  }, [googleAutocompleteReady]);

  useEffect(() => {
    let cancelled = false;

    const loadShopPickupInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/settings/shop/public`);
        const payload = await parseJsonSafely(response);
        if (!response.ok) return;

        const source = payload?.settings || {};
        if (cancelled) return;

        setShopPickupInfo({
          shopName: (source.shopLocationName || '').trim(),
          address: (source.shopLocationAddress || source.shopLocationResolvedAddress || '').trim(),
          lat: String(source.shopLocationLat || '').trim(),
          lng: String(source.shopLocationLng || '').trim(),
          regionDeliveryPrices: (source.regionDeliveryPrices && typeof source.regionDeliveryPrices === 'object') ? source.regionDeliveryPrices : {},
          cityDeliveryPrices: Array.isArray(source.cityDeliveryPrices) ? source.cityDeliveryPrices : [],
        });
      } catch {
        // Ignore pickup info fetch errors and keep fallback text.
      }
    };

    loadShopPickupInfo();

    return () => {
      cancelled = true;
    };
  }, []);

  const pickupMapUrl = useMemo(() => {
    if (shopPickupInfo.lat && shopPickupInfo.lng) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${shopPickupInfo.lat},${shopPickupInfo.lng}`)}`;
    }

    if (shopPickupInfo.address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shopPickupInfo.address)}`;
    }

    return '';
  }, [shopPickupInfo]);

  const checkoutItems = useMemo(
    () =>
      cartItems.map((item) => {
        const product = productMap.get(String(item.productId));
        const standardEnabled = product?.delivery?.standard?.enabled !== false;
        const expressEnabled = Boolean(product?.delivery?.express?.enabled);
        const selectedDelivery = deliveryChoices[item.key] || (expressEnabled && !standardEnabled ? 'express' : 'standard');

        const unitBasePrice = Number(item.price || 0);
        const unitExpressSurcharge =
          selectedDelivery === 'express' && expressEnabled ? getExpressSurcharge(product) : 0;

        const quantity = Math.max(1, Number(item.quantity || 1));
        const lineSubtotal = unitBasePrice * quantity;
        const lineSurcharge = unitExpressSurcharge * quantity;

        return {
          ...item,
          product,
          standardEnabled,
          expressEnabled,
          selectedDelivery,
          unitBasePrice,
          unitExpressSurcharge,
          lineSubtotal,
          lineSurcharge,
          lineTotal: lineSubtotal + lineSurcharge,
        };
      }),
    [cartItems, deliveryChoices, productMap]
  );

  const totals = useMemo(() => {
    const items = checkoutItems.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = checkoutItems.reduce((sum, item) => sum + item.lineSubtotal, 0);
    const expressSurcharge = checkoutItems.reduce((sum, item) => sum + item.lineSurcharge, 0);
    return {
      items,
      subtotal,
      expressSurcharge,
      grandTotal: subtotal + expressSurcharge,
    };
  }, [checkoutItems]);

  // Detect region from raw address text as fallback (when no GPS/autocomplete region set)
  const inferredRegion = useMemo(() => {
    if (detectedRegion) return detectedRegion;
    if (!deliveryAddress) return '';
    const upper = deliveryAddress.toUpperCase();
    return GHANA_REGIONS.find((r) => upper.includes(r.toUpperCase())) || '';
  }, [detectedRegion, deliveryAddress]);

  const deliveryFeeInfo = useMemo(() => {
    if (receiveMethod !== 'delivery') return null;
    const region = inferredRegion;
    const { regionDeliveryPrices = {}, cityDeliveryPrices = [] } = shopPickupInfo;

    // Check city-specific prices first (match against address text)
    if (deliveryAddress && cityDeliveryPrices.length > 0) {
      const addrUpper = deliveryAddress.toUpperCase();
      const cityMatch = cityDeliveryPrices.find(
        (c) => c.city && c.price && addrUpper.includes(c.city.toUpperCase())
      );
      if (cityMatch) {
        return { label: cityMatch.city, fee: Number(cityMatch.price) || 0, source: 'city' };
      }
    }

    // Fall back to region price
    if (region && regionDeliveryPrices[region] !== undefined && regionDeliveryPrices[region] !== '') {
      return { label: region, fee: Number(regionDeliveryPrices[region]) || 0, source: 'region' };
    }

    return null;
  }, [receiveMethod, inferredRegion, deliveryAddress, shopPickupInfo]);

  const handleProceedToPayment = async () => {
    if (checkoutItems.length === 0) return;

    setCheckoutError('');

    if (!userSession?.token) {
      onNotify?.('Please sign in to continue checkout.', 'error');
      navigate('/login?redirect=/checkout');
      return;
    }

    if (recipientName.trim().length < 2) {
      setCheckoutError('Please enter the recipient full name.');
      onNotify?.('Please enter the recipient full name.', 'error');
      return;
    }

    if (!/^\+?[0-9]{10,15}$/.test(recipientPhone.trim())) {
      setCheckoutError('Please enter a valid recipient phone number.');
      onNotify?.('Please enter a valid recipient phone number.', 'error');
      return;
    }

    if (receiveMethod === 'delivery' && deliveryAddress.trim().length < 5) {
      setCheckoutError('Please enter a delivery address to continue.');
      onNotify?.('Please enter a delivery address to continue.', 'error');
      return;
    }

    const fulfillment = {
      method: receiveMethod,
      recipientName: recipientName.trim(),
      recipientPhone: recipientPhone.trim(),
      deliveryAddress: receiveMethod === 'delivery' ? deliveryAddress.trim() : '',
      pickupAddress: receiveMethod === 'pickup' ? shopPickupInfo.address : '',
      pickupMapUrl: receiveMethod === 'pickup' ? pickupMapUrl : '',
    };

    const totalAmount = totals.grandTotal + (deliveryFeeInfo?.fee || 0);

    try {
      setPaystackRedirecting(true);

      const response = await fetch(`${API_BASE_URL}/api/payments/paystack/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userSession.token}`,
        },
        body: JSON.stringify({
          amount: totalAmount,
          currency: 'GHS',
          fulfillment,
          cart: {
            items: checkoutItems.map((item) => ({
              productId: item.productId,
              name: item.name,
              quantity: item.quantity,
              unitPrice: item.unitBasePrice,
              expressSurcharge: item.unitExpressSurcharge,
              selectedDelivery: item.selectedDelivery,
            })),
            subtotal: totals.subtotal,
            expressSurcharge: totals.expressSurcharge,
            deliveryFee: deliveryFeeInfo?.fee || 0,
            total: totalAmount,
          },
        }),
      });

      const payload = await parseJsonSafely(response);

      if (!response.ok || !payload?.authorizationUrl) {
        throw new Error(payload?.message || 'Unable to start Paystack payment right now.');
      }

      onNotify?.('Redirecting you to Paystack for secure payment...', 'info');
      window.location.assign(payload.authorizationUrl);
    } catch (error) {
      setCheckoutError(error.message || 'Unable to start Paystack payment right now.');
      onNotify?.(error.message || 'Unable to start Paystack payment right now.', 'error');
      setPaystackRedirecting(false);
    }
  };

  return (
    <div>
      <HeaderBar cartSummary={cartSummary} />

      <div className="breadcrumbs-row">
        <Link to="/">Shop</Link>
        <span>/</span>
        <Link to="/cart">Cart</Link>
        <span>/</span>
        <strong>Checkout</strong>
      </div>

      {/* Progress Indicator */}
      <div className="checkout-progress">
        <div className="checkout-progress-step checkout-progress-step--active">
          <div className="checkout-progress-icon">1</div>
          <span>Review Items</span>
        </div>
        <div className="checkout-progress-line"></div>
        <div className="checkout-progress-step checkout-progress-step--active">
          <div className="checkout-progress-icon">2</div>
          <span>Fulfillment</span>
        </div>
        <div className="checkout-progress-line"></div>
        <div className="checkout-progress-step">
          <div className="checkout-progress-icon">3</div>
          <span>Payment</span>
        </div>
      </div>

      <section className="checkout-page-layout">
        <article className="checkout-section">
          {/* Items Section Header */}
          <div className="checkout-section-header">
            <div className="checkout-section-icon">📦</div>
            <div>
              <h2>Order Items</h2>
              <p className="checkout-section-subtitle">Review and customize delivery for each item</p>
            </div>
          </div>

          {checkoutError && <div className="status-banner status-banner--error">{checkoutError}</div>}

          {checkoutItems.length === 0 ? (
            <div className="shop-state-card">
              <h3>Your cart is empty</h3>
              <p>Add products to cart before checkout.</p>
              <Link to="/" className="details-btn details-link-btn">Continue Shopping</Link>
            </div>
          ) : (
            <ul className="checkout-list">
              {checkoutItems.map((item, idx) => (
                <li key={item.key} className="checkout-item" style={{ animationDelay: `${idx * 50}ms` }}>
                  <div className="checkout-item-image-wrapper">
                    {item.image ? (
                      <img src={item.image} alt={item.name} />
                    ) : (
                      <div className="cart-image-placeholder">No Image</div>
                    )}
                    <span className="checkout-item-qty-badge">{item.quantity}</span>
                  </div>

                  <div className="checkout-item-content">
                    <h4 className="checkout-item-name">{item.name}</h4>
                    <div className="checkout-item-attrs">
                      {item.size && <span className="checkout-item-attr">Size: {item.size}</span>}
                      {item.color && <span className="checkout-item-attr">Color: {item.color}</span>}
                    </div>

                    <div className="checkout-delivery-picker">
                      <label className="checkout-delivery-option">
                        <input
                          type="radio"
                          name={`delivery-${item.key}`}
                          value="standard"
                          checked={item.selectedDelivery === 'standard'}
                          disabled={!item.standardEnabled}
                          onChange={() => setDeliveryChoices((prev) => ({ ...prev, [item.key]: 'standard' }))}
                        />
                        <span className="checkout-delivery-label">
                          <strong>Standard</strong>
                          {item.product?.delivery?.standard?.days && (
                            <small>{item.product.delivery.standard.days} days</small>
                          )}
                        </span>
                      </label>
                      <label className="checkout-delivery-option">
                        <input
                          type="radio"
                          name={`delivery-${item.key}`}
                          value="express"
                          checked={item.selectedDelivery === 'express'}
                          disabled={!item.expressEnabled}
                          onChange={() => setDeliveryChoices((prev) => ({ ...prev, [item.key]: 'express' }))}
                        />
                        <span className="checkout-delivery-label">
                          <strong>Express</strong>
                          {item.expressEnabled ? (
                            <>
                              {item.product?.delivery?.express?.days && (
                                <small>{item.product.delivery.express.days} days</small>
                              )}
                              <span className="checkout-delivery-price">+{formatCurrency(item.unitExpressSurcharge)}</span>
                            </>
                          ) : (
                            <small className="checkout-delivery-unavailable">Not available</small>
                          )}
                        </span>
                      </label>
                    </div>

                    <div className="checkout-item-pricing">
                      <div className="checkout-price-detail">
                        <span className="checkout-price-label">Subtotal</span>
                        <span className="checkout-price-value">{formatCurrency(item.lineSubtotal)}</span>
                      </div>
                      {item.lineSurcharge > 0 && (
                        <div className="checkout-price-detail">
                          <span className="checkout-price-label">Express</span>
                          <span className="checkout-price-value">{formatCurrency(item.lineSurcharge)}</span>
                        </div>
                      )}
                      <div className="checkout-price-detail checkout-price-detail--total">
                        <span className="checkout-price-label">Total</span>
                        <span className="checkout-price-value">{formatCurrency(item.lineTotal)}</span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="checkout-fulfillment-card">
            {/* Fulfillment Section Header */}
            <div className="checkout-section-header">
              <div className="checkout-section-icon">🚚</div>
              <div>
                <h3>Fulfillment Method</h3>
                <p className="checkout-section-subtitle">Choose how you'd like to receive your order</p>
              </div>
            </div>

            <div className="checkout-fulfillment-options">
              <button
                type="button"
                className={`checkout-fulfillment-option ${receiveMethod === 'pickup' ? 'active' : ''}`}
                onClick={() => setReceiveMethod('pickup')}
              >
                <span className="checkout-fulfillment-icon">🏪</span>
                <strong>Pickup in Person</strong>
                <small>Collect from our shop</small>
              </button>
              <button
                type="button"
                className={`checkout-fulfillment-option ${receiveMethod === 'delivery' ? 'active' : ''}`}
                onClick={() => setReceiveMethod('delivery')}
              >
                <span className="checkout-fulfillment-icon">📍</span>
                <strong>Home Delivery</strong>
                <small>Delivered to your address</small>
              </button>
            </div>

            {receiveMethod === 'pickup' ? (
              <div className="checkout-fulfillment-panel">
                <div className="checkout-panel-section">
                  <h4 className="checkout-panel-title">Pickup Location</h4>
                  <div className="checkout-location-card">
                    <p><strong>{shopPickupInfo.shopName || 'TK Fashion & Design'}</strong></p>
                    <p className="checkout-location-address">{shopPickupInfo.address || 'Shop location will be shared by admin soon.'}</p>
                    {pickupMapUrl && (
                      <a href={pickupMapUrl} target="_blank" rel="noreferrer" className="checkout-map-link">
                        🗺️ View on Google Maps
                      </a>
                    )}
                  </div>
                </div>

                <div className="checkout-panel-section">
                  <h4 className="checkout-panel-title">Recipient Information</h4>
                  <div className="checkout-recipient-grid">
                    <label className="checkout-form-group">
                      <span className="checkout-label-text">Full Name</span>
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(event) => setRecipientName(event.target.value)}
                        placeholder="Enter recipient full name"
                        className="checkout-input"
                      />
                    </label>
                    <label className="checkout-form-group">
                      <span className="checkout-label-text">Phone Number</span>
                      <input
                        type="tel"
                        value={recipientPhone}
                        onChange={(event) => setRecipientPhone(event.target.value)}
                        placeholder="e.g. +233XXXXXXXXX"
                        className="checkout-input"
                      />
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="checkout-fulfillment-panel">
                <div className="checkout-panel-section">
                  <h4 className="checkout-panel-title">Recipient Information</h4>
                  <div className="checkout-recipient-grid">
                    <label className="checkout-form-group">
                      <span className="checkout-label-text">Full Name</span>
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(event) => setRecipientName(event.target.value)}
                        placeholder="Enter recipient full name"
                        className="checkout-input"
                      />
                    </label>
                    <label className="checkout-form-group">
                      <span className="checkout-label-text">Phone Number</span>
                      <input
                        type="tel"
                        value={recipientPhone}
                        onChange={(event) => setRecipientPhone(event.target.value)}
                        placeholder="e.g. +233XXXXXXXXX"
                        className="checkout-input"
                      />
                    </label>
                  </div>
                </div>

                <div className="checkout-panel-section">
                  <h4 className="checkout-panel-title">Delivery Address</h4>

                  <label className="checkout-form-group">
                    <span className="checkout-label-text">GhanaPost GPS Code (Optional)</span>
                    <div className="checkout-gps-input-group">
                      <input
                        type="text"
                        value={checkoutGpsCode}
                        onChange={(event) => {
                          setCheckoutGpsCode(event.target.value);
                          setCheckoutGhanaPostError('');
                          setCheckoutGhanaPostSuccess('');
                        }}
                        placeholder="e.g. GA-183-8164"
                        maxLength={24}
                        className="checkout-input"
                      />
                      <button
                        type="button"
                        className="checkout-gps-btn"
                        onClick={() => resolveCheckoutGpsAddress(checkoutGpsCode)}
                        disabled={checkoutGhanaPostResolving || !checkoutGpsCode.trim()}
                      >
                        {checkoutGhanaPostResolving ? '⏳' : '✓'}
                      </button>
                    </div>
                    {checkoutGhanaPostSuccess && (
                      <small className="checkout-help-text checkout-help-text--success">✓ {checkoutGhanaPostSuccess}</small>
                    )}
                    {checkoutGhanaPostError && (
                      <small className="checkout-help-text checkout-help-text--error">✕ {checkoutGhanaPostError}</small>
                    )}
                  </label>

                  <label className="checkout-form-group">
                    <span className="checkout-label-text">Delivery Address</span>
                    <input
                      ref={deliveryAddressInputRef}
                      type="text"
                      value={deliveryAddress}
                      onChange={(event) => setDeliveryAddress(event.target.value)}
                      placeholder="Start typing to search or paste an address"
                      className="checkout-input"
                    />
                    {googleAutocompleteReady && (
                      <small className="checkout-help-text">💡 Start typing and choose from suggestions</small>
                    )}
                    {googleAutocompleteError && (
                      <small className="checkout-help-text checkout-help-text--error">{googleAutocompleteError}</small>
                    )}
                  </label>

                  {(userSettings?.addressResolved || userSettings?.addressLine) && (
                    <button
                      type="button"
                      className="checkout-quick-action-btn"
                      onClick={() => {
                        setDeliveryAddress((userSettings.addressResolved || userSettings.addressLine || '').trim());
                        if (userSettings?.region) setDetectedRegion(userSettings.region);
                      }}
                    >
                      📍 Use Saved Address from Settings
                    </button>
                  )}

                  {inferredRegion && (
                    <div className="checkout-region-card">
                      <div className="checkout-region-badge">
                        <span className="checkout-region-label">📍 Detected Region</span>
                        <span className="checkout-region-value">{inferredRegion}</span>
                      </div>
                      {deliveryFeeInfo ? (
                        <div className="checkout-region-fee checkout-region-fee--success">
                          <span>Delivery Fee</span>
                          <strong>{formatCurrency(deliveryFeeInfo.fee)}</strong>
                        </div>
                      ) : (
                        <div className="checkout-region-fee checkout-region-fee--pending">
                          <span>Delivery Fee</span>
                          <strong>Contact to confirm</strong>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </article>

        <aside className="checkout-summary-card">
          <div className="checkout-summary-header">
            <h3>Order Summary</h3>
            <span className="checkout-summary-badge">{totals.items} items</span>
          </div>

          <div className="checkout-summary-section">
            <div className="checkout-summary-row">
              <span className="checkout-summary-label">Subtotal</span>
              <span className="checkout-summary-value">{formatCurrency(totals.subtotal)}</span>
            </div>
            {totals.expressSurcharge > 0 && (
              <div className="checkout-summary-row">
                <span className="checkout-summary-label">Express Fees</span>
                <span className="checkout-summary-value">{formatCurrency(totals.expressSurcharge)}</span>
              </div>
            )}
            {deliveryFeeInfo && (
              <div className="checkout-summary-row">
                <span className="checkout-summary-label">Delivery ({deliveryFeeInfo.label})</span>
                <span className="checkout-summary-value checkout-summary-value--accent">{formatCurrency(deliveryFeeInfo.fee)}</span>
              </div>
            )}
            {receiveMethod === 'delivery' && !deliveryFeeInfo && deliveryAddress.trim().length > 3 && (
              <div className="checkout-summary-row">
                <span className="checkout-summary-label">Delivery Fee</span>
                <span className="checkout-summary-value checkout-summary-value--pending">Contact us</span>
              </div>
            )}
          </div>

          <div className="checkout-summary-divider"></div>

          <div className="checkout-summary-total">
            <span className="checkout-summary-label">Total Amount</span>
            <span className="checkout-summary-grand-total">{formatCurrency(totals.grandTotal + (deliveryFeeInfo?.fee || 0))}</span>
          </div>

          <button
            type="button"
            className="checkout-action-btn checkout-action-btn--primary"
            disabled={checkoutItems.length === 0 || paystackRedirecting}
            onClick={handleProceedToPayment}
          >
            {paystackRedirecting ? 'Redirecting to Paystack...' : '💳 Proceed to Payment'}
          </button>

          <Link to="/cart" className="checkout-action-btn checkout-action-btn--secondary">
            ← Back to Cart
          </Link>

          <p className="checkout-summary-note">Express fees are charged per item only when Express delivery is selected. Your delivery fee is based on your region.</p>
        </aside>
      </section>
    </div>
  );
};

const PaymentPage = ({ cartSummary }) => {
  return (
    <div>
      <HeaderBar cartSummary={cartSummary} />

      <div className="breadcrumbs-row">
        <Link to="/">Shop</Link>
        <span>/</span>
        <Link to="/checkout">Checkout</Link>
        <span>/</span>
        <strong>Payment</strong>
      </div>

      <section className="checkout-page-layout">
        <article className="checkout-section">
          <h2>Payment</h2>
          <p className="details-description">
            You are authenticated and ready to complete payment. Integrate your preferred gateway
            (card, mobile money, or bank transfer) here.
          </p>
          <p className="mini-note">Current payable amount: {formatCurrency(cartSummary.amount)}</p>
        </article>

        <aside className="checkout-summary-card">
          <h3>Secure Checkout</h3>
          <p>Signed in successfully.</p>
          <p>All payment actions should happen over secure HTTPS channels.</p>
          <Link to="/checkout" className="details-btn details-link-btn">Back to Checkout</Link>
        </aside>
      </section>
    </div>
  );
};

const ToastCenter = ({ notifications, onDismiss }) => {
  if (!notifications.length) return null;

  return (
    <div className="toast-center" role="status" aria-live="polite">
      {notifications.map((item) => (
        <div key={item.id} className={`toast-item toast-${item.type || 'info'}`}>
          <span>{item.message}</span>
          <button type="button" className="toast-close" onClick={() => onDismiss(item.id)} aria-label="Dismiss notification">×</button>
        </div>
      ))}
    </div>
  );
};

function NavProgressBar() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setActive(false);
    clearTimeout(timerRef.current);
    // Double-rAF ensures the class removal is painted before re-adding it
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setActive(true);
        timerRef.current = setTimeout(() => setActive(false), 520);
      });
    });
    return () => clearTimeout(timerRef.current);
  }, [location.key]);

  return (
    <div
      className={`tk-nav-progress${active ? ' tk-nav-progress--active' : ''}`}
      aria-hidden="true"
    />
  );
}

function AppContainer() {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Per-route SEO (product pages handle their own SEO inside ProductDetailsPage)
  const isProductRoute = location.pathname.startsWith('/product/');
  const routeSEO = useMemo(() => {
    if (isProductRoute) return null;
    const path = location.pathname;
    if (path === '/'                        ) return PAGE_SEO.home;
    if (path === '/login'                   ) return PAGE_SEO.login;
    if (path === '/register'                ) return PAGE_SEO.register;
    if (path === '/forgot-password'         ) return PAGE_SEO.forgotPassword;
    if (path === '/reset-password'          ) return PAGE_SEO.resetPassword;
    if (path === '/cart'                    ) return PAGE_SEO.cart;
    if (path === '/checkout'                ) return PAGE_SEO.checkout;
    if (path === '/payment'                 ) return PAGE_SEO.payment;
    if (path === '/settings'                ) return PAGE_SEO.settings;
    if (path === '/orders'                  ) return PAGE_SEO.orders;
    if (path.startsWith('/orders/')         ) return PAGE_SEO.orderDetails;
    if (path === '/support'                 ) return PAGE_SEO.support;
    if (path === '/notifications'           ) return PAGE_SEO.notifications;
    if (path === '/policies/acceptance'     ) return PAGE_SEO.acceptancePolicy;
    if (path === '/policies/terms'          ) return PAGE_SEO.termsPolicy;
    if (path === '/policies/privacy'        ) return PAGE_SEO.privacyPolicy;
    return PAGE_SEO.home;
  }, [location.pathname, isProductRoute]);

  useSEO(routeSEO || {});

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [mediaViews, setMediaViews] = useState({});
  const [fullImage, setFullImage] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userSession, setUserSession] = useState(null);
  const [userSettings, setUserSettings] = useState(DEFAULT_USER_SETTINGS);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  const dismissNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((message, type = 'info') => {
    if (!message) return;
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setNotifications((prev) => [...prev.slice(-3), { id, message, type }]);
    window.setTimeout(() => {
      setNotifications((prev) => prev.filter((item) => item.id !== id));
    }, 3600);
  }, []);

  // Poll unread notification count every 30s when logged in
  useEffect(() => {
    if (!userSession?.token) { setUnreadNotifCount(0); return; }
    const fetchCount = () => {
      fetch(`${API_BASE_URL}/api/notifications/my/unread-count`, {
        headers: { Authorization: `Bearer ${userSession.token}` },
      })
        .then((r) => r.json())
        .then((d) => { if (typeof d.unread === 'number') setUnreadNotifCount(d.unread); })
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, [userSession?.token]);

  const handleAuthSuccess = (session) => {
    setUserSession(session);
    localStorage.setItem(USER_SESSION_KEY, JSON.stringify(session));
  };

  const handleLogout = () => {
    setUserSession(null);
    localStorage.removeItem(USER_SESSION_KEY);
    notify('You have been logged out.', 'info');
  };

  const handleSaveUserSettings = (nextSettings) => {
    const normalized = {
      ...DEFAULT_USER_SETTINGS,
      ...nextSettings,
      fullName: nextSettings.fullName.trim(),
      phone: nextSettings.phone.trim(),
      addressLine: nextSettings.addressLine.trim(),
      addressGpsCode: nextSettings.addressGpsCode.trim(),
      addressResolved: nextSettings.addressResolved.trim(),
      addressLat: String(nextSettings.addressLat || '').trim(),
      addressLng: String(nextSettings.addressLng || '').trim(),
      addressPlaceId: String(nextSettings.addressPlaceId || '').trim(),
      city: nextSettings.city.trim(),
      region: nextSettings.region.trim(),
      landmark: nextSettings.landmark.trim(),
    };

    setUserSettings(normalized);

    if (userSession?.user) {
      const mergedSession = {
        ...userSession,
        user: {
          ...userSession.user,
          fullName: normalized.fullName || userSession.user.fullName,
          phone: normalized.phone || userSession.user.phone,
          notificationPrefs: {
            orderAlerts: Boolean(normalized.orderAlerts),
            emailUpdates: Boolean(normalized.emailUpdates),
          },
        },
      };
      setUserSession(mergedSession);
      localStorage.setItem(USER_SESSION_KEY, JSON.stringify(mergedSession));

      // Persist notification prefs to backend (fire-and-forget)
      updateNotificationPrefsRequest(userSession.token, {
        orderAlerts: Boolean(normalized.orderAlerts),
        emailUpdates: Boolean(normalized.emailUpdates),
      }).catch((err) => {
        console.error('Failed to save notification preferences:', err);
      });
    }

    notify('Settings saved successfully.', 'success');
  };

  const handleDeleteOwnAccount = async ({ reason, details }) => {
    if (!userSession?.token) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    await deleteOwnAccountRequest(userSession.token, { reason, details });

    setUserSession(null);
    setCartItems([]);
    setUserSettings(DEFAULT_USER_SETTINGS);
    localStorage.removeItem(USER_SESSION_KEY);
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(USER_SETTINGS_STORAGE_KEY);

    notify('Your account was deleted successfully.', 'info');
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_BASE_URL}/api/products?limit=100`);
        const payload = await parseJsonSafely(response);

        if (!response.ok) {
          throw new Error(payload.message || 'Unable to load products right now');
        }

        if (!cancelled) {
          setProducts(payload.products || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load products right now');
          notify(err.message || 'Unable to load products right now', 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        setCartItems(parsed);
      }
    } catch {
      // Ignore malformed local cart data and continue with an empty cart.
    }
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(USER_SETTINGS_STORAGE_KEY);
      if (!saved) return;

      const parsed = JSON.parse(saved);
      setUserSettings((prev) => ({ ...prev, ...parsed }));
    } catch {
      // Ignore malformed settings and continue with defaults.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        const saved = localStorage.getItem(USER_SESSION_KEY);
        if (!saved) return;

        const parsed = JSON.parse(saved);
        if (!parsed?.token) {
          localStorage.removeItem(USER_SESSION_KEY);
          return;
        }

        const profile = await getUserProfileRequest(parsed.token);
        if (!cancelled) {
          const normalized = { token: parsed.token, user: profile.user };
          setUserSession(normalized);
          localStorage.setItem(USER_SESSION_KEY, JSON.stringify(normalized));
        }
      } catch {
        if (!cancelled) {
          setUserSession(null);
          localStorage.removeItem(USER_SESSION_KEY);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(userSettings));
  }, [userSettings]);

  useEffect(() => {
    if (!userSession?.user) return;

    setUserSettings((prev) => ({
      ...prev,
      fullName: prev.fullName || userSession.user.fullName || '',
      phone: prev.phone || userSession.user.phone || '',
      orderAlerts: userSession.user.notificationPrefs?.orderAlerts !== false,
      emailUpdates: userSession.user.notificationPrefs?.emailUpdates !== false,
    }));
  }, [userSession?.user]);

  const cartSummary = useMemo(() => {
    const quantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const amount = cartItems.reduce((sum, item) => sum + item.quantity * (item.price || 0), 0);
    return { quantity, amount };
  }, [cartItems]);

  const upsertCartItem = (product, selection, existingKey = null) => {
    const size = selection.size || '';
    const color = selection.color || '';
    const quantity = Math.max(1, Number(selection.quantity || 1));
    const pricingResult = getPricingForSelection(product, { size, color, quantity });
    const price = pricingResult.unitPrice;
    const nextKey = buildCartKey(product._id, size, color);

    let notifyMessage = 'Item added to cart.';

    setCartItems((prev) => {
      const withoutExisting = existingKey ? prev.filter((item) => item.key !== existingKey) : prev;
      const already = withoutExisting.find((item) => item.key === nextKey);

      if (already) {
        notifyMessage = `Updated quantity for ${product.name}.`;
        return withoutExisting.map((item) =>
          item.key === nextKey
            ? (() => {
              const updatedQuantity = item.quantity + quantity;
              const updatedPricing = getPricingForSelection(product, {
                size: item.size || '',
                color: item.color || '',
                quantity: updatedQuantity,
              });

              return {
                ...item,
                quantity: updatedQuantity,
                price: updatedPricing.unitPrice,
                pricingNote: updatedPricing.note,
              };
            })()
            : item
        );
      }

      return [
        ...withoutExisting,
        {
          key: nextKey,
          productId: product._id,
          name: product.name,
          category: product.category,
          image: resolveMediaUrl(product.images?.[0]),
          size: size || null,
          color: color || null,
          quantity,
          price,
          pricingNote: pricingResult.note,
        },
      ];
    });

    notify(notifyMessage, 'success');
  };

  const quickAddToCart = (product, quantity) => {
    const defaults = getDefaultSelection(product);
    upsertCartItem(product, { ...defaults, quantity });
  };

  const updateCartQuantity = (key, quantity) => {
    const safeQuantity = Math.max(1, quantity);
    let productName = 'item';
    setCartItems((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;

        productName = item.name || 'item';

        const product = products.find((p) => String(p._id) === String(item.productId));
        if (!product) {
          return { ...item, quantity: safeQuantity };
        }

        const pricingResult = getPricingForSelection(product, {
          size: item.size || '',
          color: item.color || '',
          quantity: safeQuantity,
        });

        return {
          ...item,
          quantity: safeQuantity,
          price: pricingResult.unitPrice,
          pricingNote: pricingResult.note,
        };
      })
    );

    notify(`Quantity updated for ${productName}.`, 'info');
  };

  const removeCartItem = (key) => {
    let removedName = 'item';
    setCartItems((prev) => {
      const found = prev.find((item) => item.key === key);
      if (found?.name) removedName = found.name;
      return prev.filter((item) => item.key !== key);
    });
    notify(`${removedName} removed from cart.`, 'info');
  };

  useEffect(() => {
    if (products.length === 0) return;

    setCartItems((prev) =>
      prev.map((item) => {
        const product = products.find((p) => String(p._id) === String(item.productId));
        if (!product) return item;

        const pricingResult = getPricingForSelection(product, {
          size: item.size || '',
          color: item.color || '',
          quantity: item.quantity,
        });

        return {
          ...item,
          price: pricingResult.unitPrice,
          pricingNote: pricingResult.note,
        };
      })
    );
  }, [products]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (window.innerWidth > 900) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = sidebarOpen ? 'hidden' : previousOverflow || '';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  return (
    <div className="app-shell">
      <NavProgressBar />
      <SidebarNav
        cartSummary={cartSummary}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        userSession={userSession}
        onLogout={handleLogout}
        unreadNotifCount={unreadNotifCount}
      />

      <div className="app-main-column">
        <div className="mobile-topbar">
          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(true)}
            aria-expanded={sidebarOpen}
            aria-controls="user-sidebar-nav"
          >
            ☰ Menu
          </button>
          <Link to="/cart" className="mobile-cart-link">
            Cart ({cartSummary.quantity})
          </Link>
        </div>

        <div className="shop-page">
          <div key={location.key} className="tk-page-enter">
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <HeaderBar cartSummary={cartSummary} />
                  <ShopPage
                    products={products}
                    loading={loading}
                    error={error}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    activeCategory={activeCategory}
                    setActiveCategory={setActiveCategory}
                    quantityDrafts={quantityDrafts}
                    setQuantityDrafts={setQuantityDrafts}
                    quickAddToCart={quickAddToCart}
                    mediaViews={mediaViews}
                    setMediaViews={setMediaViews}
                    onOpenImage={setFullImage}
                  />
                </>
              }
            />
            <Route
              path="/cart"
              element={
                <CartPage
                  cartItems={cartItems}
                  cartSummary={cartSummary}
                  updateCartQuantity={updateCartQuantity}
                  removeCartItem={removeCartItem}
                />
              }
            />
            <Route
              path="/checkout"
              element={
                <CheckoutPage
                  products={products}
                  cartItems={cartItems}
                  cartSummary={cartSummary}
                  userSession={userSession}
                  userSettings={userSettings}
                  onNotify={notify}
                />
              }
            />
            <Route
              path="/payment"
              element={
                userSession?.token
                  ? <PaymentPage cartSummary={cartSummary} />
                  : <Navigate to="/login?redirect=/payment" replace />
              }
            />
            <Route
              path="/settings"
              element={
                userSession?.token
                  ? (
                    <UserSettingsPage
                      cartSummary={cartSummary}
                      userSession={userSession}
                      settings={userSettings}
                      onSaveSettings={handleSaveUserSettings}
                      onDeleteAccount={handleDeleteOwnAccount}
                    />
                  )
                  : <Navigate to="/login?redirect=/settings" replace />
              }
            />
            <Route
              path="/orders"
              element={
                userSession?.token
                  ? <MyOrdersPage cartSummary={cartSummary} />
                  : <Navigate to="/login?redirect=/orders" replace />
              }
            />
            <Route
              path="/orders/:orderNumber"
              element={
                userSession?.token
                  ? <OrderDetailsPage products={products} />
                  : <Navigate to="/login?redirect=/orders" replace />
              }
            />
            <Route
              path="/support"
              element={
                userSession?.token
                  ? <SupportPage onNotify={notify} />
                  : <Navigate to="/login?redirect=/support" replace />
              }
            />
            <Route
              path="/notifications"
              element={
                userSession?.token
                  ? <NotificationsPage onNavigate={(path) => navigate(path)} />
                  : <Navigate to="/login?redirect=/notifications" replace />
              }
            />
            <Route
              path="/login"
              element={<AuthPage mode="login" userSession={userSession} onAuthSuccess={handleAuthSuccess} onNotify={notify} />}
            />
            <Route
              path="/register"
              element={<AuthPage mode="register" userSession={userSession} onAuthSuccess={handleAuthSuccess} onNotify={notify} />}
            />
            <Route
              path="/forgot-password"
              element={<ForgotPasswordPage onNotify={notify} />}
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage onNotify={notify} />}
            />
            <Route
              path="/product/:productId"
              element={
                <ProductDetailsPage
                  products={products}
                  cartItems={cartItems}
                  upsertCartItem={upsertCartItem}
                  cartSummary={cartSummary}
                  onOpenImage={setFullImage}
                />
              }
            />
            <Route path="/policies/acceptance" element={<AcceptancePolicyPage />} />
            <Route path="/policies/terms" element={<TermsPolicyPage />} />
            <Route path="/policies/privacy" element={<PrivacyPolicyPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </div>
        </div>

        <ShopFooter />
      </div>

      {userSession?.token && !location.pathname.startsWith('/support') && (
        <Link
          to="/support"
          className="floating-support-btn"
          aria-label="Open support"
        >
          <span className="floating-support-icon">💬</span>
          <span className="floating-support-text">Support</span>
        </Link>
      )}

      <ImageLightbox image={fullImage} onClose={() => setFullImage(null)} />
      <ToastCenter notifications={notifications} onDismiss={dismissNotification} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContainer />
    </BrowserRouter>
  );
}

export default App;
